import { useEffect } from "react";

/**
 * Hook used to execute a function when clicking outside
 * a specific element.
 * @param ref Reference to the element of interest.
 * @param handler Function that controls the execution of the hook.
 * @param enabled (Optional) Boolean that controls if the hook
 * is activated or not. If it's true, it's activated. `Default: true`.
 * @param closeOnAnyClick (Optional) Boolean that forces the 
 * handler execution by clicking wherever. If it's false, it will 
 * execute the handler just on an outside click. `Default: false`.
 */
const useMouseClickOutside = <T extends HTMLElement = HTMLElement>(
  ref: React.RefObject<T>,
  handler: (event: MouseEvent | TouchEvent) => void,
  enabled: boolean = true,
  closeOnAnyClick: boolean = false
) => {
  // Hook definition
  useEffect(() => {
    // Check if the hook is enabled
    if (!enabled) {
      return;
    }

    // Define the function to execute on click/touch outside
    const handleOutsideEvent = (event: MouseEvent | TouchEvent): void => {
      if (closeOnAnyClick || (ref.current && !ref.current.contains(event.target as Node))) {
        handler(event);
      }
    };

    // Add event listeners
    document.addEventListener("mousedown", handleOutsideEvent);
    document.addEventListener("touchstart", handleOutsideEvent);

    // Clean up event listeners
    return () => {
      document.removeEventListener("mousedown", handleOutsideEvent);
      document.removeEventListener("touchstart", handleOutsideEvent);
    };
  }, [ref, handler, enabled]);
};

export default useMouseClickOutside;
